import { Badge } from "@comps/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import User from "@dto/Users";
import { TW_RecordSelectPopoverContent, TW_FilterEditorRecordSelect, TW_SelectionRecordBadge, TW_SelectRecordCommandItem, TW_SelectRecordCommandItemRecordText, TW_SelectRecordCommandItemSelected, TW_SelectRecordScrollArea, TW_FilterEditorRecordSelectTriggerDisabled, TW_FilterEditorRecordSelectTrigger, TW_SelectionRecordBadgeMore, TW_CommonCommandInput, getBadgeStyle, useHover, getWsButtonStyle, TW_SelectUserCommandList } from "@utils/twClasses";
import { userToString } from "@utils/types";
import { CURRENT_USER } from "@utils/users";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { useModelStore } from "src/store";


type UserSelectProps = {
    allUsers: User[];
    selectedUsers: User[];
    disabled: boolean;
    currentUserSelected: boolean;
    className?: string;
    onSelectionChange: (users: User[], currentUserSelected: boolean) => void;
    allowCurrentUserSelection?: boolean;
}

export function UserSelect({ allUsers, selectedUsers, disabled, currentUserSelected, className, onSelectionChange, allowCurrentUserSelection = true }: UserSelectProps) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsColor = useModelStore(state => state.wsColor);

    const badgeStyle = useMemo(() => {
        return getBadgeStyle(wsColor);
    }, [wsColor]);

    const users = selectedUsers.filter(user => user.username);
    let firstUser: User | null = null;
    let firstUserValueStr = null;
    if (!currentUserSelected) {
        firstUser = (users.length > 0) ? users[0] : null;
        firstUserValueStr = firstUser ? firstUser.username : null;
    }

    const nextUsers = selectedUsers.filter(user => user._key !== firstUser?._key);
    const nextTooltip = nextUsers.map(user => user.username).join(", ");
    return (
        <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
            <PopoverTrigger disabled={disabled} className={(disabled ? " " + TW_FilterEditorRecordSelectTriggerDisabled : TW_FilterEditorRecordSelectTrigger) + (className ? " " + className : "")}>
                <div className={TW_FilterEditorRecordSelect + (className ? " " + className : "")}>
                    {currentUserSelected && <Badge key={CURRENT_USER} className={TW_SelectionRecordBadge} style={badgeStyle}>{t("data.user.currentUser.label")}</Badge>}
                    {!currentUserSelected && firstUserValueStr && <Badge key={firstUser?._key} className={TW_SelectionRecordBadge} style={badgeStyle}>{firstUserValueStr}</Badge>}
                    {nextUsers.length > 0 && <Badge key={nextUsers[0]?._key} className={TW_SelectionRecordBadgeMore} tooltip={nextTooltip} style={badgeStyle}>{"+" + nextUsers.length}</Badge>}
                </div>
            </PopoverTrigger>
            <PopoverContent className={TW_RecordSelectPopoverContent}>
                <UserSelectForm allUsers={allUsers} selectedUsers={selectedUsers} currentUserSelected={currentUserSelected} onSelectionChange={onSelectionChange} allowCurrentUserSelection={allowCurrentUserSelection} />
            </PopoverContent>
        </Popover>
    )
}


interface UserSelectFormProps {
    allUsers: User[];
    selectedUsers: User[];
    currentUserSelected: boolean;
    onSelectionChange: (users: User[], currentUserSelected: boolean) => void;
    allowCurrentUserSelection?: boolean;
}

function UserSelectForm({ allUsers, selectedUsers, currentUserSelected, onSelectionChange, allowCurrentUserSelection }: UserSelectFormProps) {

    const selectedUserKeys = selectedUsers.map(user => user._key);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);

    const onUserSelect = (userKey: string) => {
        if (userKey === CURRENT_USER) {
            onCurrentUserSelect(!currentUserSelected);
            return;
        }
        if (selectedUserKeys.includes(userKey)) {
            onSelectionChange(selectedUsers.filter(user => user._key !== userKey), currentUserSelected);
        } else {
            onSelectionChange([...selectedUsers, allUsers.find(user => user._key === userKey)!], currentUserSelected);
        }
    }

    const onCurrentUserSelect = (selected: boolean) => {
        const selectedUsers = selectedUserKeys.map(key => allUsers.find(user => user._key === key)).filter(user => user !== undefined);
        onSelectionChange(selectedUsers as User[], selected);
    }

    const currentUserLabel = t("data.user.currentUser.label");

    const scrollAreaStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
        scrollbarThumbColor: wsDarkColor,
        scrollbarTrackColor: wsLightColor,
    }

    if (allUsers.length === 0) {
        return (
            <div>
                <h1>{t('data.table.noRecords')}</h1>
            </div>
        )
    } else {
        return (

            <Command>
                <CommandInput placeholder={t("data.record.search.placeholder")} className={TW_CommonCommandInput} autoFocus={true} />
                <div className={TW_SelectRecordScrollArea}>
                    <CommandList className={TW_SelectUserCommandList} style={scrollAreaStyle}>
                        <CommandEmpty>{t("data.table.noRecords")}</CommandEmpty>
                        <CommandGroup>
                            {allowCurrentUserSelection && <UserSelectItem key={CURRENT_USER} userKey={CURRENT_USER} label={currentUserLabel} isSelected={currentUserSelected} onUserSelect={onUserSelect} />}
                            {allUsers.map((user) => {
                                const valueStr = userToString(user);
                                return (
                                    <UserSelectItem key={user._key} userKey={user._key} label={valueStr} isSelected={selectedUserKeys.includes(user._key)} onUserSelect={onUserSelect} />
                                )
                            })}
                        </CommandGroup>
                    </CommandList>
                </div>
            </Command>

        )
    }
}

function UserSelectItem({ userKey, label, isSelected, onUserSelect }: { userKey: string, label: string, isSelected: boolean, onUserSelect: (userKey: string) => void }) {

    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const useButtonHover = useHover(getWsButtonStyle(), getWsButtonStyle(wsLightColor, wsDarkColor));

    const selectedRecordStyle = {
        backgroundColor: wsLightColor,
        borderColor: wsDarkColor,
    }

    return (
        <CommandItem
            key={label}
            className={`${isSelected ? TW_SelectRecordCommandItemSelected : TW_SelectRecordCommandItem}`}
            onSelect={() => onUserSelect(userKey)}
            style={isSelected ? selectedRecordStyle : undefined}
            onMouseEnter={useButtonHover.onMouseEnter}
            onMouseLeave={useButtonHover.onMouseLeave}>
            <span className={TW_SelectRecordCommandItemRecordText}>{label}</span>
        </CommandItem>
    )
}

